import { UIElement } from "../../common_ui_elements/ui_element.js";
import { getLinkedProviderInfo, getLinkedSocialProviders, initLinkedProvidersFromServer, LinkedSocialProvider, resetLinkedSocialProviders, toAuthProvider } from "../account_providers_preview_state.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { AuthSessionStore } from "../../../api/auth_session.js";
import { DialogManager } from "../dialog_manager.js";
import { AbstractDialog } from "../abstract_dialog.js";
import { DialogId } from "../dialog_id.js";
import { SaveDialog } from "./save_dialog.js";
import type { PlayerDto } from "../../../api/dto/player.dto.js";
import { ApiError } from "../../../api/http_client.js";
import { GoogleSignInPlugin } from "../../../plugins/play_games_plugin.js";
import { FacebookSignInPlugin } from "../../../plugins/facebook_sign_in_plugin.js";
import { ExitFromGameAccountDialog } from "./exit_from_game_account_dialog.js";
import { DisconnectAccountDialog } from "./disconnect_account_dialog.js";
import { FailedDisconnectSocialMediaDialog } from "../failed_disconnect_social_media_dialog.js";
import { applePlugin, clearFacebookAvatar } from "../../../api/account_link_service.js";
import { AuthMessageDialog } from "./auth_message_dialog.js";
import { DeleteAccountDialog } from "./delete_account_dialog.js";
import { disconnectProvider as disconnectProviderApi } from "../../../api/requests/auth.requests.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { Vector2 } from "../../../common/vector2.js";

const PROVIDER_ID_CACHE_KEYS: Record<LinkedSocialProvider, string> = {
  [ LinkedSocialProvider.Google ]:   'web_google_id',
  [ LinkedSocialProvider.Facebook ]: 'web_facebook_id',
  [ LinkedSocialProvider.Apple ]:    'native_apple_id',
};


enum SocialSectionMode
{
  Linked,
  Empty
}

class UISocialMediaSection extends UIElement
{
  private static readonly EMPTY_ACCENT_COLOR = "#1A3E9F";

  private mode: SocialSectionMode;
  private provider?: LinkedSocialProvider;
  private display_name?: string;
  private display_id?: string;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    mode: SocialSectionMode,
    provider?: LinkedSocialProvider,
    on_click?: () => void,
    display_name?: string,
    display_id?: string,
  )
  {
    super( ctx, x, y, w, h, false, !!on_click );
    this.mode = mode;
    this.provider = provider;
    this.display_name = display_name;
    this.display_id = display_id;
    this.createElements();

    if ( on_click )
      this.setOnClick( on_click );
  }

  private createElements(): void
  {
    if ( this.mode === SocialSectionMode.Linked && this.provider !== undefined )
    {
      this.createSocialMediaSectionBg();
      this.createSocialMediaIcon();
      this.createSocialMediaText();
      return;
    }

    this.createEmptySectionBg();
    this.createPlusIcon();
  }

  private createSocialMediaSectionBg(): void
  {
    const social_media_section_background = new RoundedRectangle(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      LayoutScaler.cornerRadiusFromDesign( 75 ),
      "#1A3E9F",
    );

    this.addChild( social_media_section_background, true );
  }

  private createSocialMediaIcon(): void
  {
    if ( this.provider === undefined ) return;

    const icon_x = LayoutScaler.xFromDesign( 29 );
    const icon_size = LayoutScaler.widthFromDesign( 60 );
    const icon_y = ( this.rect.getH() - icon_size ) / 2;

    const social_media_icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_size,
      icon_size,
      this.getProviderIcon( this.provider )
    );

    this.addChild( social_media_icon, true );
  }

  private createSocialMediaText(): void
  {
    if ( this.provider === undefined ) return;

    const text_x = LayoutScaler.xFromDesign( 120 );
    const text_w = LayoutScaler.widthFromDesign( 610 );
    const name_h = LayoutScaler.heightFromDesign( 45 );
    const name_y = LayoutScaler.yFromDesign( 15 );

    if ( this.display_name )
    {
      const name_text = new UITextElement(
        this.ctx,
        text_x,
        name_y,
        text_w,
        name_h,
        {
          text: this.display_name,
          vertical_align: MyTextVerticalAlign.Center,
          text_align: MyTextAlign.Left,
          text_style: TextStyleScheme.geologica,
          font_weight: 700,
          font_size: LayoutScaler.textSizeFromDesign( 35 ),
          text_color: ColorScheme.white
        }
      );
      this.addChild( name_text, true );
    }

    if ( this.display_id )
    {
      const only_id = !this.display_name;
      const id_y    = only_id ? 0 : LayoutScaler.yFromDesign( 68 );
      const id_h    = only_id ? this.rect.getH() : LayoutScaler.heightFromDesign( 34 );

      const id_text = new UITextElement(
        this.ctx,
        text_x,
        id_y,
        text_w,
        id_h,
        {
          text: only_id ? this.display_id : `ID: ${ this.display_id }`,
          vertical_align: MyTextVerticalAlign.Center,
          text_align: MyTextAlign.Left,
          text_style: TextStyleScheme.geologica,
          font_weight: only_id ? 700 : 400,
          font_size: LayoutScaler.textSizeFromDesign( only_id ? 35 : 22 ),
          text_color: ColorScheme.white
        }
      );
      this.addChild( id_text, true );
    }
  }

  private createEmptySectionBg(): void
  {
    const empty_section_background = new RoundedRectangle(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      LayoutScaler.cornerRadiusFromDesign( 75 ),
      "rgba(0, 0, 0, 0)",
      undefined,
      false,
      UISocialMediaSection.EMPTY_ACCENT_COLOR,
      LayoutScaler.widthFromDesign( 4 )
    );

    this.addChild( empty_section_background, true );
  }

  private createPlusIcon(): void
  {
    const plus_size = LayoutScaler.widthFromDesign( 46 );
    const bar_thickness = LayoutScaler.widthFromDesign( 8 );
    const plus_x = ( this.rect.getW() - plus_size ) / 2;
    const plus_y = ( this.rect.getH() - plus_size ) / 2;

    const horizontal_bar = new RoundedRectangle(
      this.ctx,
      plus_x,
      plus_y + plus_size / 2 - bar_thickness / 2,
      plus_size,
      bar_thickness,
      bar_thickness / 2,
      UISocialMediaSection.EMPTY_ACCENT_COLOR
    );
    this.addChild( horizontal_bar, true );

    const vertical_bar = new RoundedRectangle(
      this.ctx,
      plus_x + plus_size / 2 - bar_thickness / 2,
      plus_y,
      bar_thickness,
      plus_size,
      bar_thickness / 2,
      UISocialMediaSection.EMPTY_ACCENT_COLOR
    );
    this.addChild( vertical_bar, true );
  }

  private getProviderIcon( provider: LinkedSocialProvider ): ImgID
  {
    switch ( provider )
    {
      case LinkedSocialProvider.Google:   return ImgID.GoogleIcon;
      case LinkedSocialProvider.Facebook: return ImgID.FacebookIcon;
      case LinkedSocialProvider.Apple:    return ImgID.AppleIcon;
    }
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "UISocialMediaSection";
  }
}

class UIExitFromAccountSection extends UIElement
{
  private exit_button!: RoundedRectangle;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
  )
  {
    super( ctx, x, y, w, h );
    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createExitButton();
    this.createUserIdText();
  }

  private createBackground(): void
  {
    const exit_background = new RoundedRectangle(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      LayoutScaler.cornerRadiusFromDesign( 75 ),
      "#1A3E9F",
    );

    this.addChild( exit_background );
  }

  private createExitButton(): void
  {
    const btn_y = LayoutScaler.yFromDesign( 77 );
    const btn_w = LayoutScaler.widthFromDesign( 727 );
    const btn_x = ( this.rect.getW() - btn_w ) / 2;
    const btn_h = LayoutScaler.heightFromDesign( 110 );
    this.exit_button = new RoundedRectangle(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      LayoutScaler.cornerRadiusFromDesign( 70 ),
      "#0080FF",
      undefined,
      true,
      "#1A3E9F",
      LayoutScaler.widthFromDesign( 4 )
    );
    this.exit_button.setOnClick( () =>
    {
      DialogManager.openDialog( new ExitFromGameAccountDialog() );
    } );

    this.addChild( this.exit_button );

    const text_w = btn_w;
    const text_h = btn_h;

    const exit_btn_text = new UITextElement(
      this.ctx,
      btn_x,
      btn_y,
      text_w,
      text_h,
      {
        localization_key: "CLOSE_GAME_DIALOG.EXIT_BUTTON",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( 47 ),
        font_weight: 700,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    this.addChild( exit_btn_text, true );
  }

  private createUserIdText(): void
  {
    const user_id = AuthSessionStore.getPlayerId() ?? 0;

    const text_y = LayoutScaler.yFromDesign( 17 );
    const text_w = LayoutScaler.widthFromDesign( 444 );
    const text_x = ( this.rect.getW() - text_w ) / 2;
    const text_h = LayoutScaler.heightFromDesign( 40 );

    const user_id_text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        text: `user_id: ${ user_id }`,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 700,
        font_size: LayoutScaler.textSizeFromDesign( 30 )
      }
    );

    this.addChild( user_id_text );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "UIExitFromAccountSection";
  }
}

export class AccountProgressSavedDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.ACCOUNT_PROGRESS_SAVED_DIALOG;
  private bg!: UINineSliceElement;
  private close_btn!: UIImage;
  private is_disconnecting = false;

  constructor( private linked_providers: LinkedSocialProvider[] = getLinkedSocialProviders() )
  {
    const body_w = LayoutScaler.widthFromDesignAdaptive( 909 );
    const body_h = LayoutScaler.heightFromDesign( 1113 );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createTitle();
    this.createSocialMediaSections();
    this.createDeleteAccountText();
    this.createExitFromAccountSection();
    this.createCloseButton();
  }

  private createSocialMediaSections(): void
  {
    const section_w = LayoutScaler.widthFromDesign( 760 );
    const section_h = LayoutScaler.heightFromDesign( 110 );
    const section_x = ( this.rect.getW() - section_w ) / 2;
    const section_y_slots = [ 260, 381, 504 ];

    const sections: Array<{ mode: SocialSectionMode; provider?: LinkedSocialProvider; on_click?: () => void; display_name?: string; display_id?: string }> =
      this.linked_providers.slice( 0, 3 ).map( provider =>
      {
        const info = getLinkedProviderInfo( provider );
        const email_only = provider === LinkedSocialProvider.Google || provider === LinkedSocialProvider.Apple;
        return {
          mode: SocialSectionMode.Linked,
          provider,
          on_click:     () => { DialogManager.openDialog( new DisconnectAccountDialog( provider, () => { void this.disconnectProvider( provider ); } ) ); },
          display_name: email_only ? undefined : ( info?.name ?? undefined ),
          display_id:   email_only
            ? ( info?.email ?? AccountProgressSavedDialog.getCachedIdForProvider( provider ) ?? undefined )
            : ( AccountProgressSavedDialog.getCachedIdForProvider( provider ) ?? undefined ),
        };
      } );

    if ( sections.length < 3 )
    {
      sections.push( {
        mode: SocialSectionMode.Empty,
        on_click: () =>
        {
          console.log( "[AccountProgressSavedDialog] Plus social section clicked" );
          DialogManager.openDialog( new SaveDialog( undefined, this.linked_providers ) );
        }
      } );
    }

    sections.forEach( ( section, index ) =>
    {
      const section_y = LayoutScaler.yFromDesign( section_y_slots[ index ] );
      const social_media = new UISocialMediaSection(
        this.ctx,
        section_x,
        section_y,
        section_w,
        section_h,
        section.mode,
        section.provider,
        section.on_click,
        section.display_name,
        section.display_id,
      );

      this.addChild( social_media );
    } );
  }

  private static getCachedIdForProvider( provider: LinkedSocialProvider ): string | null
  {
    return localStorage.getItem( PROVIDER_ID_CACHE_KEYS[ provider ] );
  }

  private async signOutProvider( provider: LinkedSocialProvider ): Promise<void>
  {
    switch ( provider )
    {
      case LinkedSocialProvider.Google:   await GoogleSignInPlugin.signOut(); return;
      case LinkedSocialProvider.Facebook: await FacebookSignInPlugin.signOut(); return;
      case LinkedSocialProvider.Apple:    await applePlugin().signOut(); return;
    }
  }

  private async disconnectProvider( provider: LinkedSocialProvider ): Promise<void>
  {
    if ( this.is_disconnecting ) return;
    this.is_disconnecting = true;

    let player: PlayerDto;
    try
    {
      player = await disconnectProviderApi( toAuthProvider( provider ) );
    }
    catch ( e )
    {
      this.is_disconnecting = false;
      if ( e instanceof ApiError && e.status === 400 &&
           ( e.body as any )?.error === 'Cannot disconnect the last linked account' )
      {
        DialogManager.openDialog( new FailedDisconnectSocialMediaDialog( provider ) );
        return;
      }
      console.warn( `[AccountProgressSavedDialog] disconnect ${ LinkedSocialProvider[ provider ] } failed:`, e );
      DialogManager.openDialog( AuthMessageDialog.forDisconnectError( provider, e ) );
      return;
    }

    await Promise.allSettled( [ this.signOutProvider( provider ) ] );
    localStorage.removeItem( PROVIDER_ID_CACHE_KEYS[ provider ] );
    if ( provider === LinkedSocialProvider.Facebook )
      await clearFacebookAvatar();
    resetLinkedSocialProviders();
    initLinkedProvidersFromServer( player.linked_providers ?? [] );
    console.log( `[AccountProgressSavedDialog] Disconnected provider: ${ LinkedSocialProvider[ provider ] }` );

    if ( getLinkedSocialProviders().length > 0 )
    {
      DialogManager.routeToDialog( new AccountProgressSavedDialog() );
      return;
    }

    DialogManager.closeAfterTransition();
  }

  private createTitle(): void
  {
    const label_w = this.rect.getW() * 0.8;
    const label_h = LayoutScaler.heightFromDesign( 154 );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( 72 );

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "PROGRESS_SAVED_DIALOG.TITLE",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 20 ),
        outline_color: "#103A92",
      }
    );

    this.addChild( label );
  }

  private createBackground(): void
  {
    this.bg = new UINineSliceElement(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( this.bg );
  }

  private createExitFromAccountSection(): void
  {
    const section_y = LayoutScaler.yFromDesign( 700 );
    const section_w = LayoutScaler.widthFromDesign( 760 );
    const section_x = ( this.rect.getW() - section_w ) / 2;
    const section_h = LayoutScaler.heightFromDesign( 201 );

    const section = new UIExitFromAccountSection(
      this.ctx,
      section_x,
      section_y,
      section_w,
      section_h,
    );

    this.addChild( section );
  }

  private createDeleteAccountText(): void
  {
    const del_account_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 1007 ),
      this.rect.getW(),
      LayoutScaler.heightFromDesign( 40 ),
      {
        localization_key: "PROGRESS_SAVED_DIALOG.ACCOUNT_DELETE",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 700,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );

    del_account_text.setOnClick( () =>
    {
      DialogManager.openDialog( new DeleteAccountDialog() );
    } );
    this.addChild( del_account_text );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 117;

    const btn_size = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_x = this.rect.getW() - btn_size + LayoutScaler.widthFromDesign( 10 );
    const btn_y = -LayoutScaler.yFromDesign( 100 );

    this.close_btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_size,
      btn_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    this.close_btn.setOnClick( () => this.close() );
    this.addChild( this.close_btn );
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "AccountProgressSavedDialog";
  }
}
